
import { useEffect, useState } from 'react'
import { Outlet, Link, useLocation } from 'react-router-dom'
import { toAbsoluteUrl } from '../../../_myb/helpers'
import { brandSettings } from '../settings/BrandSettings'
import { APP_VERSION } from '../../Constants'
import { getCurrentLang, setLanguage } from '../../../_myb/i18n/LangConfig'
import '../../routing/routeTransition.css'
import './authHero.css'

const LANGUAGES = [
  { lang: 'en', name: 'English',  flag: 'united-states.svg' },
  { lang: 'de', name: 'German',   flag: 'germany.svg'       },
  { lang: 'es', name: 'Spanish',  flag: 'spain.svg'         },
  { lang: 'fr', name: 'French',   flag: 'france.svg'        },
  { lang: 'ja', name: 'Japanese', flag: 'japan.svg'         },
  { lang: 'zh', name: 'Mandarin', flag: 'china.svg'         },
  { lang: 'ta', name: 'Tamil',    flag: 'india.svg'         },
]

// Stored untranslated; translated at render time (see CLAUDE.md i18n rules)
const WHATS_NEW = [
  { title: 'AI Bank Reconciliation', body: 'Match bank transactions to your books with AI-suggested entries and rules.' },
  { title: 'AI Dashboard Brief', body: 'A daily summary of your cash, sales and what needs attention.' },
  { title: 'Audit Trail', body: 'See who changed what, and when, across every record.' },
  { title: 'Projects', body: 'Track job costing and profitability per engagement.' },
  { title: 'Budgets', body: 'Plan and monitor spending by account.' },
  { title: 'Production Orders', body: 'Manufacturing and assembly workflows.' },
]

const AuthLayout = () => {
  const location = useLocation()
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [newsIdx, setNewsIdx] = useState(0)
  const [newsPaused, setNewsPaused] = useState(false)
  const currentLang = getCurrentLang()
  const currentLanguage = LANGUAGES.find(l => l.lang === currentLang) ?? LANGUAGES[0]

  useEffect(() => {
    const root = document.getElementById('root')
    if (root) {
      root.style.height = '100%'
    }
    return () => {
      if (root) {
        root.style.height = 'auto'
      }
    }
  }, [])

  // Auto-rotate the What's New carousel (paused on hover/focus)
  useEffect(() => {
    if (newsPaused) return
    const id = window.setInterval(() => setNewsIdx(i => (i + 1) % WHATS_NEW.length), 4500)
    return () => window.clearInterval(id)
  }, [newsPaused])

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!dropdownOpen) return
    const handler = () => setDropdownOpen(false)
    document.addEventListener('click', handler)
    return () => document.removeEventListener('click', handler)
  }, [dropdownOpen])

  const brand = import.meta.env.VITE_APP_LABEL || 'mybooks'
  const isSignupRoute = location.pathname.includes('signup')

  return (
    <div className='myb-auth-hero'>
      {/* begin::Topbar */}
      <div className='myb-auth-topbar'>
        <Link to='/'>
          <img alt='Logo' src={toAbsoluteUrl(`media/logos/${brand}-logo-linear-dark-2.svg`)} className='h-35px' />
        </Link>

        {!brandSettings.hideSignup && (
          <div className='d-flex align-items-center'>
            {isSignupRoute ? (
              <>
                <span className='myb-auth-switch-hint'>{'Already have an account?'.toTranslatedDisplay()}</span>
                <Link to='/auth/login' className='myb-auth-switch-link'>{'Sign in'.toTranslatedDisplay()}</Link>
              </>
            ) : (
              <>
                <span className='myb-auth-switch-hint'>{'Not a Member yet?'.toTranslatedDisplay()}</span>
                <Link to='/auth/signup' className='myb-auth-switch-link'>{'Create account'.toTranslatedDisplay()}</Link>
              </>
            )}
          </div>
        )}
      </div>
      {/* end::Topbar */}

      {/* begin::Body */}
      <div className='myb-auth-body'>
        {/* begin::Card */}
        <div className='myb-auth-card-wrap'>
          <div className='myb-auth-card'>
            <div key={location.pathname} className='myb-route-transition'>
              <Outlet />
            </div>

            {/* begin::Card footer */}
            <div className='myb-auth-card-footer'>
              {/* begin::Language Switcher */}
              <div className='myb-lang-switch' onClick={e => e.stopPropagation()}>
                <button
                  type='button'
                  className='btn btn-sm myb-lang-switch-btn'
                  onClick={() => setDropdownOpen(o => !o)}
                >
                  <img
                    src={toAbsoluteUrl(`media/flags/${currentLanguage.flag}`)}
                    alt={currentLanguage.name}
                    className='rounded-1'
                    style={{ width: 16, height: 16, objectFit: 'cover' }}
                  />
                  <span className='fw-semibold'>{currentLanguage.name}</span>
                  <i className={`bi bi-chevron-${dropdownOpen ? 'up' : 'down'} fs-8`} />
                </button>

                {dropdownOpen && (
                  <div
                    className='bg-white border border-gray-200 rounded shadow-sm py-2'
                    style={{ position: 'absolute', bottom: '110%', left: 0, minWidth: 160, zIndex: 1050 }}
                  >
                    {LANGUAGES.map(l => (
                      <button
                        key={l.lang}
                        type='button'
                        className={`btn btn-sm w-100 text-start d-flex align-items-center gap-3 px-4 py-2 rounded-0 ${l.lang === currentLang ? 'bg-light-primary text-primary fw-bold' : 'text-gray-700'}`}
                        onClick={() => { setLanguage(l.lang); setDropdownOpen(false) }}
                      >
                        <img
                          src={toAbsoluteUrl(`media/flags/${l.flag}`)}
                          alt={l.name}
                          className='rounded-1'
                          style={{ width: 18, height: 18, objectFit: 'cover' }}
                        />
                        {l.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {/* end::Language Switcher */}

              {/* begin::Links */}
              {!brandSettings.hideAuthFooter && (
                <div className='myb-auth-card-links'>
                  <a href='https://zetran.com/termsofservice'>{'Terms'.toTranslatedDisplay()}</a>
                  <a href='https://zetran.com/privacypolicy'>{'Privacy Policy'.toTranslatedDisplay()}</a>
                  <a href='#' target='_blank'>{'Contact Us'.toTranslatedDisplay()}</a>
                </div>
              )}
              {/* end::Links */}
            </div>
            {/* end::Card footer */}
          </div>
        </div>
        {/* end::Card */}

        {/* begin::Marketing */}
        <div className='myb-auth-marketing'>
          <span className='myb-badge-pill'>{'RELEASE'.toTranslatedDisplay()} {APP_VERSION}</span>
          <h1 className='myb-auth-headline'>
            {'Fast, efficient'.toTranslatedDisplay()}<br />
            {'and productive'.toTranslatedDisplay()}<span className='myb-auth-headline-dot'>.</span>
          </h1>
          <p className='myb-auth-lede'>
            {'Accounting, projects and production in one place — with AI that keeps the ledger clean while you work.'.toTranslatedDisplay()}
          </p>

          {brandSettings.showForMainBrand ? (
            <div
              className='myb-auth-news'
              onMouseEnter={() => setNewsPaused(true)}
              onMouseLeave={() => setNewsPaused(false)}
              onFocus={() => setNewsPaused(true)}
              onBlur={() => setNewsPaused(false)}
            >
              <div className='myb-auth-news-head'>
                <span className='myb-auth-news-dot' />
                <span>{"WHAT'S NEW".toTranslatedDisplay()}</span>
              </div>
              <div key={newsIdx} className='myb-auth-news-slide' aria-live='polite'>
                <div className='myb-auth-news-title'>{WHATS_NEW[newsIdx].title.toTranslatedDisplay()}</div>
                <div className='myb-auth-news-body'>{WHATS_NEW[newsIdx].body.toTranslatedDisplay()}</div>
              </div>
              <div className='myb-auth-news-dots'>
                {WHATS_NEW.map((item, i) => (
                  <button
                    key={item.title}
                    type='button'
                    aria-label={item.title.toTranslatedDisplay()}
                    className={`myb-auth-news-pip${i === newsIdx ? ' active' : ''}`}
                    onClick={() => setNewsIdx(i)}
                  />
                ))}
              </div>
            </div>
          ) : brandSettings.appLoginText ? (
            brandSettings.appLoginText
          ) : (
            <div className='text-white fs-base'>
              {'Welcome to'.toTranslatedDisplay()} {' '}
              <a href='https://mybooksai.app' className='opacity-75-hover text-warning fw-bold me-1'>
                myBooksAI
              </a>
              {' '}{'– the new version of myBooks accounting software!'.toTranslatedDisplay()} <br />
              {'Need assistance? Visit our'.toTranslatedDisplay()}{' '}
              <a href='https://mybooksai.app/help' className='opacity-75-hover text-warning fw-bold me-1'>
                {'Help Portal'.toTranslatedDisplay()}
              </a>
              {' '}{'for guidance and support.'.toTranslatedDisplay()}
            </div>
          )}
        </div>
        {/* end::Marketing */}
      </div>
      {/* end::Body */}
    </div>
  )
}

export { AuthLayout }
